<template>
    <div v-if="visibleFields.length" class="custom-fields-form">
        <div v-if="title" class="custom-fields-form__title">
            <i class="fas fa-list-ul"></i>
            <span>{{ title }}</span>
        </div>

        <b-row>
            <b-col
                v-for="field in visibleFields"
                :key="field.key"
                :md="getFieldColumnMd(field)"
            >
                <b-form-group
                    :label="getFieldLabel(field)"
                    :label-for="getFieldId(field)"
                    class="mb-3"
                >
                    <template v-if="editable">
                        <AppTextarea
                            v-if="field.type === 'textarea'"
                            :id="getFieldId(field)"
                            :value="getFieldValue(field)"
                            :placeholder="field.placeholder || ''"
                            :required="field.required === true"
                            :maxlength="field.maxlength || null"
                            :state="getFieldState(field)"
                            @input="setFieldValue(field, $event)"
                        />

                        <b-form-input
                            v-else-if="field.type === 'number'"
                            :id="getFieldId(field)"
                            :value="getFieldValue(field)"
                            :placeholder="field.placeholder || ''"
                            :required="field.required === true"
                            :min="field.min || null"
                            :max="field.max || null"
                            :step="field.step || 'any'"
                            :state="getFieldState(field)"
                            type="number"
                            @input="setFieldValue(field, $event)"
                        />

                        <b-form-input
                            v-else-if="field.type === 'date'"
                            :id="getFieldId(field)"
                            :value="getFieldValue(field)"
                            :required="field.required === true"
                            :state="getFieldState(field)"
                            type="date"
                            @input="setFieldValue(field, $event)"
                        />

                        <b-form-input
                            v-else-if="field.type === 'datetime'"
                            :id="getFieldId(field)"
                            :value="getFieldValue(field)"
                            :required="field.required === true"
                            :state="getFieldState(field)"
                            type="datetime-local"
                            @input="setFieldValue(field, $event)"
                        />

                        <div
                            v-else-if="isDateRangeField(field)"
                            class="custom-fields-form__range"
                        >
                            <b-form-input
                                :id="getFieldId(field)"
                                :value="getRangeBoundaryValue(field, 'from')"
                                :required="field.required === true"
                                :state="getFieldState(field)"
                                :type="getDateRangeInputType(field)"
                                placeholder="С"
                                @input="setRangeBoundaryValue(field, 'from', $event)"
                            />
                            <span class="custom-fields-form__range-separator">по</span>
                            <b-form-input
                                :id="`${getFieldId(field)}-to`"
                                :value="getRangeBoundaryValue(field, 'to')"
                                :required="field.required === true"
                                :state="getFieldState(field)"
                                :type="getDateRangeInputType(field)"
                                placeholder="По"
                                @input="setRangeBoundaryValue(field, 'to', $event)"
                            />
                        </div>

                        <b-form-radio-group
                            v-else-if="field.type === 'checkbox'"
                            :id="getFieldId(field)"
                            :checked="getFieldValue(field)"
                            :options="[{text: 'Нет', value: false}, {text: 'Да', value: true}]"
                            buttons
                            button-variant="outline-primary"
                            size="sm"
                            @change="setFieldValue(field, $event)"
                        />

                        <v-select
                            v-else-if="isDropdownField(field)"
                            :id="getFieldId(field)"
                            :value="getDropdownValue(field)"
                            :options="getDropdownOptions(field)"
                            :multiple="isMultipleDropdownField(field)"
                            :filterable="true"
                            :searchable="true"
                            label="label"
                            :placeholder="field.placeholder || 'Выберите значение'"
                            :clearable="field.required !== true"
                            @input="setDropdownValue(field, $event)"
                        >
                            <template #no-options>
                                <span>Нет вариантов</span>
                            </template>
                        </v-select>

                        <v-select
                            v-else-if="isReferenceField(field)"
                            :id="getFieldId(field)"
                            :value="getReferenceValue(field)"
                            :options="getReferenceOptions(field)"
                            :multiple="field.type === 'reference_multiple'"
                            :taggable="field.allow_manual_entries === true"
                            :push-tags="field.allow_manual_entries === true"
                            :disabled="isReferenceLoading(field)"
                            label="label"
                            :placeholder="field.placeholder || 'Выберите значение'"
                            :clearable="field.required !== true"
                            @input="setReferenceValue(field, $event)"
                        >
                            <template #no-options>
                                <span>{{ getReferenceNoOptionsText(field) }}</span>
                            </template>
                        </v-select>

                        <b-form-input
                            v-else
                            :id="getFieldId(field)"
                            :value="getFieldValue(field)"
                            :placeholder="field.placeholder || ''"
                            :required="field.required === true"
                            :maxlength="field.maxlength || null"
                            :state="getFieldState(field)"
                            @input="setFieldValue(field, $event)"
                        />

                        <b-form-invalid-feedback :state="getFieldState(field)">
                            Заполните поле
                        </b-form-invalid-feedback>
                        <EmployeeLink v-for="person in referencePeople(field).filter(person => person.id)" :key="person.key"
                            :user-id="person.id" :fallback="person.name" compact icon-only class="mr-2" />
                    </template>

                    <div
                        v-else
                        class="custom-fields-form__readonly"
                        :class="{ 'custom-fields-form__readonly--empty': isEmptyField(field) }"
                    >
                        <template v-if="referencePeople(field).length">
                            <span v-for="person in referencePeople(field)" :key="person.key" class="mr-2">
                                <EmployeeLink :user-id="person.id" :fallback="person.name" compact />
                            </span>
                        </template>
                        <template v-else>{{ formatFieldValue(field) }}</template>
                    </div>
                </b-form-group>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import EmployeeLink from '@/components/EmployeeLink.vue';
export default {
    components: {EmployeeLink},
    name: 'CustomFieldsForm',
    props: {
        fields: {
            type: Array,
            default: () => []
        },
        value: {
            type: Object,
            default: () => ({})
        },
        editable: {
            type: Boolean,
            default: true
        },
        title: {
            type: String,
            default: ''
        },
        idPrefix: {
            type: String,
            default: 'custom-field'
        },
        requestTypeId: {
            type: [Number, String],
            default: null
        }
    },
    data() {
        return {
            referenceOptions: {},
            referenceLoading: {}
        };
    },
    computed: {
        normalizedFields() {
            return (Array.isArray(this.fields) ? this.fields : [])
                .filter(field => field && typeof field === 'object')
                .map(field => ({
                    ...field,
                    key: String(field.key || '').trim(),
                    label: String(field.label || '').trim(),
                    type: String(field.type || 'text').trim() || 'text'
                }))
                .filter(field => field.key && field.label);
        },
        visibleFields() {
            return this.getVisibleFieldsForValues(this.value || {});
        }
    },
    watch: {
        normalizedFields: {
            handler() {
                this.loadReferenceOptions();
            },
            immediate: true
        }
    },
    methods: {
        referencePeople(field) {
            if (!this.isReferenceField(field) || field.reference !== 'users') return [];
            const value = this.getFieldValue(field);
            return (Array.isArray(value) ? value : [value]).filter(Boolean).map((item, index) => {
                const normalized = this.normalizeReferenceValue(item) || {};
                const id = !normalized.manual && Number(normalized.id);
                return {key: index, id: Number.isInteger(id) && id > 0 ? id : null, name: this.formatReferenceItem(item)};
            });
        },
        getFieldColumnMd(field) {
            return field.type === 'textarea' || this.isDateRangeField(field) ? 12 : 6;
        },
        getFieldId(field) {
            return `${this.idPrefix}-${field.key}`;
        },
        getFieldLabel(field) {
            return field.required === true ? `${field.label} *` : field.label;
        },
        getFieldValue(field) {
            const source = this.value || {};
            if (field.type === 'checkbox') {
                const value = source[field.key];
                return typeof value === 'string'
                    ? ['true', '1', 'yes', 'да'].includes(value.trim().toLowerCase())
                    : Boolean(value);
            }
            return Object.prototype.hasOwnProperty.call(source, field.key)
                ? source[field.key]
                : (field.type === 'checkbox' ? false : '');
        },
        setFieldValue(field, value) {
            const next = {
                ...(this.value || {}),
                [field.key]: value
            };
            this.$emit('input', this.cleanHiddenValues(next));
        },
        isDropdownField(field) {
            return field.type === 'select_single' || field.type === 'select_multiple';
        },
        isDateRangeField(field) {
            return field.type === 'date_range' || field.type === 'datetime_range';
        },
        getDateRangeInputType(field) {
            return field.type === 'datetime_range' ? 'datetime-local' : 'date';
        },
        getRangeBoundaryValue(field, boundary) {
            const value = this.getFieldValue(field);
            if (!value || typeof value !== 'object' || Array.isArray(value)) {
                return '';
            }
            if (boundary === 'from') {
                return value.from || value.start || value.date_from || value.dateFrom || '';
            }
            return value.to || value.end || value.date_to || value.dateTo || '';
        },
        setRangeBoundaryValue(field, boundary, value) {
            const current = this.getFieldValue(field);
            const range = current && typeof current === 'object' && !Array.isArray(current)
                ? {
                    from: this.getRangeBoundaryValue(field, 'from'),
                    to: this.getRangeBoundaryValue(field, 'to')
                }
                : { from: '', to: '' };
            range[boundary] = value;
            this.setFieldValue(field, range);
        },
        isMultipleDropdownField(field) {
            return field.type === 'select_multiple';
        },
        isReferenceField(field) {
            return field.type === 'reference_single' || field.type === 'reference_multiple';
        },
        isReferenceLoading(field) {
            return this.referenceLoading[field.reference] === true;
        },
        getReferenceOptions(field) {
            if (Array.isArray(field.options) && field.options.length) {
                return field.options.map(option => this.normalizeOption(option)).filter(Boolean);
            }
            return this.referenceOptions[field.reference] || [];
        },
        getDropdownOptions(field) {
            const options = Array.isArray(field.options) ? field.options : [];
            return options
                .map(option => this.normalizeOption(option))
                .map(option => {
                    if (!option) {
                        return null;
                    }
                    let value = option.value !== undefined
                        ? option.value
                        : (option.id !== undefined ? option.id : option.label);
                    if ((value === undefined || value === null || String(value).trim() === '') && option.label) {
                        value = option.label;
                    }
                    if (value === undefined || value === null || String(value).trim() === '') {
                        return null;
                    }
                    return {
                        ...option,
                        id: value,
                        value,
                        label: option.label
                    };
                })
                .filter(Boolean);
        },
        getReferenceNoOptionsText(field) {
            if (field.allow_manual_entries === true) {
                return 'Введите значение и нажмите Enter';
            }
            return this.isReferenceLoading(field) ? 'Загрузка...' : 'Нет вариантов';
        },
        getDropdownValue(field) {
            const value = this.getFieldValue(field);
            if (this.isMultipleDropdownField(field)) {
                const list = Array.isArray(value) ? value : (value ? [value] : []);
                return list.map(item => this.matchDropdownOption(field, item)).filter(Boolean);
            }
            return this.matchDropdownOption(field, value);
        },
        setDropdownValue(field, value) {
            if (this.isMultipleDropdownField(field)) {
                const list = Array.isArray(value) ? value : (value ? [value] : []);
                this.setFieldValue(field, list.map(item => this.normalizeDropdownValue(field, item)).filter(Boolean));
                return;
            }
            this.setFieldValue(field, this.normalizeDropdownValue(field, value));
        },
        getReferenceValue(field) {
            const value = this.getFieldValue(field);
            if (field.type === 'reference_multiple') {
                const list = Array.isArray(value) ? value : (value ? [value] : []);
                return list.map(item => this.matchReferenceOption(field, item)).filter(Boolean);
            }
            return this.matchReferenceOption(field, value);
        },
        setReferenceValue(field, value) {
            if (field.type === 'reference_multiple') {
                const list = Array.isArray(value) ? value : (value ? [value] : []);
                this.setFieldValue(field, list.map(item => this.normalizeReferenceValue(item)).filter(Boolean));
                return;
            }
            this.setFieldValue(field, this.normalizeReferenceValue(value));
        },
        normalizeOption(option) {
            if (option === null || option === undefined || option === '') {
                return null;
            }
            if (typeof option === 'string' || typeof option === 'number') {
                return {
                    id: option,
                    label: String(option)
                };
            }
            const id = option.id !== undefined ? option.id : option.value;
            const label = option.label || option.text || option.name || option.object_name || '';
            if (id === undefined && !label) {
                return null;
            }
            return {
                ...option,
                id,
                label: String(label || id)
            };
        },
        normalizeReferenceValue(value) {
            if (value === null || value === undefined || value === '') {
                return null;
            }
            if (typeof value === 'string') {
                const label = value.trim();
                return label ? { label, manual: true } : null;
            }
            if (typeof value === 'number') {
                return { id: value, label: String(value) };
            }
            if (typeof value === 'object') {
                const option = this.normalizeOption(value);
                if (!option) {
                    return null;
                }
                if (option.manual === true || option.isManual === true || option.id === undefined) {
                    return { label: option.label, manual: true };
                }
                return {
                    id: option.id,
                    label: option.label
                };
            }
            return null;
        },
        normalizeDropdownValue(field, value) {
            const option = this.matchDropdownOption(field, value);
            if (!option) {
                return null;
            }
            return {
                value: option.value !== undefined ? option.value : option.id,
                label: option.label
            };
        },
        matchDropdownOption(field, value) {
            if (this.isEmptyValue(value)) {
                return null;
            }
            const sourceTokens = this.getComparableTokens(value);
            if (!sourceTokens.length) {
                return null;
            }
            return this.getDropdownOptions(field).find(option => {
                const optionTokens = this.getComparableTokens({
                    id: option.value,
                    value: option.value,
                    label: option.label
                });
                return this.hasMatchingToken(optionTokens, sourceTokens);
            }) || null;
        },
        matchReferenceOption(field, value) {
            const normalized = this.normalizeReferenceValue(value);
            if (!normalized) {
                return null;
            }
            if (normalized.manual === true || normalized.id === undefined) {
                return normalized;
            }
            const option = this.getReferenceOptions(field).find(item =>
                String(item.id) === String(normalized.id)
            );
            return option || normalized;
        },
        isEmptyValue(value) {
            if (value === undefined || value === null) return true;
            if (typeof value === 'string') return value.trim() === '';
            if (Array.isArray(value)) return value.length === 0;
            if (typeof value === 'object' && (
                Object.prototype.hasOwnProperty.call(value, 'from') ||
                Object.prototype.hasOwnProperty.call(value, 'to') ||
                Object.prototype.hasOwnProperty.call(value, 'start') ||
                Object.prototype.hasOwnProperty.call(value, 'end') ||
                Object.prototype.hasOwnProperty.call(value, 'date_from') ||
                Object.prototype.hasOwnProperty.call(value, 'date_to')
            )) {
                return ![
                    value.from,
                    value.to,
                    value.start,
                    value.end,
                    value.date_from,
                    value.date_to,
                    value.dateFrom,
                    value.dateTo
                ].some(item => item !== undefined && item !== null && String(item).trim() !== '');
            }
            if (typeof value === 'object') return Object.keys(value).length === 0;
            return false;
        },
        getFieldVisibilityCondition(field) {
            const condition = field && (
                field.visibility ||
                field.visibility_condition ||
                field.show_if ||
                field.showIf
            );
            if (!condition || typeof condition !== 'object' || condition.enabled === false) {
                return null;
            }
            const fieldKey = String(
                condition.field_key ||
                condition.fieldKey ||
                condition.source_field_key ||
                condition.sourceFieldKey ||
                ''
            ).trim();
            if (!fieldKey) {
                return null;
            }
            return {
                fieldKey,
                operator: String(condition.operator || 'equals').trim() || 'equals',
                value: condition.value !== undefined ? condition.value : condition.values
            };
        },
        normalizeComparableToken(value) {
            if (value === undefined || value === null) {
                return '';
            }
            return String(value).trim().toLowerCase();
        },
        getComparableTokens(value) {
            if (this.isEmptyValue(value)) {
                return [];
            }
            if (Array.isArray(value)) {
                return value.reduce((result, item) => result.concat(this.getComparableTokens(item)), []);
            }
            if (typeof value === 'object') {
                return ['id', 'value', 'key', 'label', 'text', 'name']
                    .map(prop => value[prop])
                    .filter(item => item !== undefined && item !== null && item !== '')
                    .map(item => this.normalizeComparableToken(item))
                    .filter(Boolean);
            }
            return [this.normalizeComparableToken(value)].filter(Boolean);
        },
        getExpectedTokens(value) {
            if (Array.isArray(value)) {
                return value.reduce((result, item) => result.concat(this.getComparableTokens(item)), []);
            }
            if (typeof value === 'string') {
                return value
                    .split(',')
                    .map(item => this.normalizeComparableToken(item))
                    .filter(Boolean);
            }
            return this.getComparableTokens(value);
        },
        hasMatchingToken(sourceTokens, expectedTokens) {
            return sourceTokens.some(token => expectedTokens.indexOf(token) !== -1);
        },
        isFieldVisibleForValues(field, values) {
            const condition = this.getFieldVisibilityCondition(field);
            if (!condition) {
                return true;
            }
            const sourceValue = Object.prototype.hasOwnProperty.call(values || {}, condition.fieldKey)
                ? values[condition.fieldKey]
                : undefined;
            const sourceFilled = !this.isEmptyValue(sourceValue);
            const sourceTokens = this.getComparableTokens(sourceValue);
            const expectedTokens = this.getExpectedTokens(condition.value);

            if (condition.operator === 'filled') {
                return sourceFilled;
            }
            if (condition.operator === 'empty') {
                return !sourceFilled;
            }
            if (condition.operator === 'not_equals' || condition.operator === 'not_in') {
                return sourceFilled && !this.hasMatchingToken(sourceTokens, expectedTokens);
            }
            return this.hasMatchingToken(sourceTokens, expectedTokens);
        },
        getVisibleFieldsForValues(values) {
            const source = values || {};
            const visibleKeys = new Set();
            const visibleValues = {};
            return this.normalizedFields.filter(field => {
                const isVisible = this.isFieldVisibleForValues(field, visibleValues);
                if (isVisible) {
                    visibleKeys.add(field.key);
                    if (Object.prototype.hasOwnProperty.call(source, field.key)) {
                        visibleValues[field.key] = source[field.key];
                    } else if (field.type === 'checkbox') {
                        visibleValues[field.key] = false;
                    }
                }
                return isVisible;
            });
        },
        cleanHiddenValues(values) {
            const source = values || {};
            const visibleKeys = new Set(this.getVisibleFieldsForValues(source).map(field => field.key));
            return Object.keys(source).reduce((result, key) => {
                if (visibleKeys.has(key)) {
                    result[key] = source[key];
                }
                return result;
            }, {});
        },
        isEmptyField(field) {
            return this.isEmptyValue(this.getFieldValue(field));
        },
        getFieldState(field) {
            if (field.required !== true) {
                return null;
            }
            return !this.isEmptyField(field);
        },
        formatFieldValue(field) {
            const value = this.getFieldValue(field);
            if (this.isEmptyValue(value)) {
                return 'Не указано';
            }
            if (field.type === 'checkbox') {
                return value ? 'Да' : 'Нет';
            }
            if (field.type === 'date') {
                return this.formatDateTimeValue(value, false);
            }
            if (field.type === 'datetime') {
                return this.formatDateTimeValue(value, true);
            }
            if (this.isDateRangeField(field)) {
                return this.formatDateRangeValue(value, field.type === 'datetime_range');
            }
            if (Array.isArray(value)) {
                return value.map(item => this.formatReferenceItem(item)).filter(Boolean).join(', ') || 'Не указано';
            }
            if (typeof value === 'object') {
                return this.formatReferenceItem(value) || 'Не указано';
            }
            return String(value);
        },
        formatDateTimeValue(value, includeTime) {
            if (value === undefined || value === null || value === '') {
                return '';
            }

            const text = String(value).trim();
            const match = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);
            let year;
            let month;
            let day;
            let hour = '';
            let minute = '';

            if (match) {
                year = Number(match[1]);
                month = Number(match[2]);
                day = Number(match[3]);
                hour = match[4] || '';
                minute = match[5] || '';
            } else {
                const parsed = new Date(text);
                if (Number.isNaN(parsed.getTime())) {
                    return text;
                }
                year = parsed.getFullYear();
                month = parsed.getMonth() + 1;
                day = parsed.getDate();
                hour = String(parsed.getHours()).padStart(2, '0');
                minute = String(parsed.getMinutes()).padStart(2, '0');
            }

            const date = new Date(year, month - 1, day);
            if (Number.isNaN(date.getTime())) {
                return text;
            }

            const weekdays = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
            const datePart = [
                String(day).padStart(2, '0'),
                String(month).padStart(2, '0'),
                String(year)
            ].join('.');
            const weekday = weekdays[date.getDay()];
            const timePart = includeTime && hour && minute ? `, ${hour}:${minute}` : '';

            return `${datePart} ${weekday}${timePart}`;
        },
        formatDateRangeValue(value, includeTime) {
            if (!value || typeof value !== 'object' || Array.isArray(value)) {
                return 'Не указано';
            }
            const from = value.from || value.start || value.date_from || value.dateFrom || '';
            const to = value.to || value.end || value.date_to || value.dateTo || '';
            const fromText = this.formatDateTimeValue(from, includeTime);
            const toText = this.formatDateTimeValue(to, includeTime);
            if (fromText && toText) {
                return `с ${fromText} по ${toText}`;
            }
            if (fromText) {
                return `с ${fromText}`;
            }
            if (toText) {
                return `по ${toText}`;
            }
            return 'Не указано';
        },
        formatReferenceItem(item) {
            if (item === null || item === undefined) {
                return '';
            }
            if (typeof item === 'string' || typeof item === 'number') {
                return String(item);
            }
            return String(item.label || item.text || item.name || item.id || '').trim();
        },
        async loadReferenceOptions() {
            const refs = this.normalizedFields
                .filter(field => this.isReferenceField(field) && field.reference && !Array.isArray(field.options))
                .map(field => field.reference)
                .filter((reference, index, list) => list.indexOf(reference) === index);

            for (const reference of refs) {
                if (this.referenceOptions[reference] || this.referenceLoading[reference]) {
                    continue;
                }
                await this.loadReference(reference);
            }
        },
        async loadReference(reference) {
            this.$set(this.referenceLoading, reference, true);
            try {
                let options = [];
                if (reference === 'users') {
                    options = await this.loadUsersReference();
                } else if (reference === 'departments') {
                    options = await this.loadDepartmentsReference();
                } else if (reference === 'objects' || reference === 'objects_in_process') {
                    options = await this.loadObjectsReference();
                } else if (reference === 'materials') {
                    options = await this.loadMaterialsReference();
                } else if (reference === 'material_groups') {
                    options = await this.loadMaterialGroupsReference();
                } else if (reference === 'work_types') {
                    options = await this.loadWorkTypesReference();
                } else if (reference === 'work_groups') {
                    options = await this.loadWorkGroupsReference();
                }
                this.$set(this.referenceOptions, reference, options);
            } catch (error) {
                console.error(`Ошибка загрузки справочника ${reference}:`, error);
                this.$set(this.referenceOptions, reference, []);
            } finally {
                this.$set(this.referenceLoading, reference, false);
            }
        },
        async loadUsersReference() {
            if (!this.$store.state.users || !this.$store.state.users.length) {
                await this.$store.dispatch('getUsers', { full: true }).catch(() => {});
            }
            return (this.$store.state.users || [])
                .filter(user => user && user.active !== false && user.isBot !== true)
                .map(user => {
                    const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
                    const phone = user.phone ? `, ${this.$formatPhone(user.phone)}` : '';
                    return {
                        id: user.user_id,
                        label: `${name || user.login || `Пользователь #${user.user_id}`}${phone}`
                    };
                })
                .filter(option => option.id)
                .sort((a, b) => a.label.localeCompare(b.label, 'ru'));
        },
        async loadDepartmentsReference() {
            if (!this.$store.state.departments || !this.$store.state.departments.length) {
                await this.$store.dispatch('getDepartments').catch(() => {});
            }
            return (this.$store.state.departments || [])
                .filter(department => department && department.is_active !== false)
                .map(department => ({
                    id: department.id,
                    label: department.name || `Отдел #${department.id}`
                }))
                .filter(option => option.id)
                .sort((a, b) => a.label.localeCompare(b.label, 'ru'));
        },
        async loadObjectsReference() {
            const params = this.requestTypeId ? { request_type_id: this.requestTypeId } : {};
            const { data } = await this.$http.get('/api/requests/building-processes', { params });
            return (Array.isArray(data) ? data : [])
                .map(process => {
                    const id = process.value || process.building_process_id || process.id;
                    const contract = process.contract_name ? ` (${process.contract_name})` : '';
                    const name = process.text || process.object_name || process.name || '';
                    return {
                        id,
                        label: name ? `${name}${contract}` : `Объект #${id}`
                    };
                })
                .filter(option => option.id)
                .sort((a, b) => a.label.localeCompare(b.label, 'ru'));
        },
        async loadMaterialsReference() {
            const data = await this.$store.dispatch('requests_getMaterials', this.requestTypeId || null)
                .catch(() => []);
            return (Array.isArray(data) ? data : [])
                .map(material => {
                    const id = material.id || material.material_id || material.value;
                    const name = material.name || material.text || material.material_name || '';
                    const unit = material.unit_of_measurement || material.unit ? `, ${material.unit_of_measurement || material.unit}` : '';
                    return {
                        id,
                        label: name ? `${name}${unit}` : `Материал #${id}`
                    };
                })
                .filter(option => option.id)
                .sort((a, b) => a.label.localeCompare(b.label, 'ru'));
        },
        async loadMaterialGroupsReference() {
            if (!this.$store.state.materials ||
                !Array.isArray(this.$store.state.materials.groups) ||
                !this.$store.state.materials.groups.length) {
                await this.$store.dispatch('getMaterialGroups').catch(() => {});
            }
            const groups = this.$store.state.materials && Array.isArray(this.$store.state.materials.groups)
                ? this.$store.state.materials.groups
                : [];
            return groups
                .map(group => ({
                    id: group.id || group.value,
                    label: group.name || group.text || `Группа материалов #${group.id || group.value}`
                }))
                .filter(option => option.id)
                .sort((a, b) => a.label.localeCompare(b.label, 'ru'));
        },
        async loadWorkTypesReference() {
            if (!Array.isArray(this.$store.state.workTypes) || !this.$store.state.workTypes.length) {
                await this.$store.dispatch('getWorkTypes').catch(() => {});
            }
            return (this.$store.state.workTypes || [])
                .map(type => ({
                    id: type.id || type.value,
                    label: type.name || type.text || `Вид работ #${type.id || type.value}`
                }))
                .filter(option => option.id)
                .sort((a, b) => a.label.localeCompare(b.label, 'ru'));
        },
        async loadWorkGroupsReference() {
            if (!Array.isArray(this.$store.state.workGroups) || !this.$store.state.workGroups.length) {
                await this.$store.dispatch('getWorkGroups').catch(() => {});
            }
            return (this.$store.state.workGroups || [])
                .map(group => ({
                    id: group.id || group.value,
                    label: group.name || group.text || `Группа работ #${group.id || group.value}`
                }))
                .filter(option => option.id)
                .sort((a, b) => a.label.localeCompare(b.label, 'ru'));
        }
    }
};
</script>

<style scoped>
.custom-fields-form {
    margin-top: 1rem;
}

.custom-fields-form__title {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.75rem;
}

.custom-fields-form__readonly {
    min-height: 1.5rem;
    white-space: pre-line;
}

.custom-fields-form__readonly--empty {
    color: #6c757d;
    font-style: italic;
}

.custom-fields-form__range {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    gap: 0.5rem;
    align-items: center;
}

.custom-fields-form__range-separator {
    color: #6c757d;
    font-size: 0.9rem;
}

.custom-fields-form >>> input::placeholder,
.custom-fields-form >>> textarea::placeholder,
.custom-fields-form >>> .vs__search::placeholder {
    color: #adb5bd;
    opacity: 1;
}

.custom-fields-form >>> .vs__search::-webkit-input-placeholder {
    color: #adb5bd;
}

.custom-fields-form >>> .vs__search::-moz-placeholder {
    color: #adb5bd;
    opacity: 1;
}

.custom-fields-form >>> .vs__dropdown-toggle {
    min-height: 38px;
    border-color: #ced4da;
}

@media (max-width: 576px) {
    .custom-fields-form__range {
        grid-template-columns: minmax(0, 1fr);
    }

    .custom-fields-form__range-separator {
        display: none;
    }
}
</style>
